const SESSION_KEY = "vantero.menu.session"

// Read synchronously so the axios interceptor can reach it without React state.
export function getSession() {
  try {
    const raw = localStorage.getItem(SESSION_KEY)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

export function saveSession(session) {
  localStorage.setItem(SESSION_KEY, JSON.stringify(session))
}

export function clearSession() {
  localStorage.removeItem(SESSION_KEY)
}

const LANGUAGE_KEY = "vantero.menu.language"
const LANGUAGE_CATALOGUE_KEY = "vantero.menu.languages"

// Bundled fallback, not the source of truth: used only until GET /languages/ has
// answered once on this device. The API decides what is selectable.
export const LANGUAGES = [
  { code: "en", name: "English", native_name: "English", is_default: true },
  { code: "es", name: "Spanish", native_name: "Español", is_default: false }
]

// Parsed catalogue, so the axios interceptor doesn't re-read localStorage per request.
let catalogueCache = null

function readCatalogue() {
  if (catalogueCache) return catalogueCache
  try {
    const parsed = JSON.parse(localStorage.getItem(LANGUAGE_CATALOGUE_KEY))
    const entries = normalizeCatalogue(parsed)
    catalogueCache = entries.length ? entries : LANGUAGES
  } catch {
    catalogueCache = LANGUAGES
  }
  return catalogueCache
}

// Drops anything without a usable code so a truncated cache can't empty the picker.
function normalizeCatalogue(list) {
  if (!Array.isArray(list)) return []
  return list
    .map((entry) => ({
      code: String(entry?.code ?? "").trim().toLowerCase(),
      name: String(entry?.name ?? "").trim(),
      native_name: String(entry?.native_name ?? "").trim(),
      is_default: Boolean(entry?.is_default)
    }))
    .filter((entry) => entry.code)
    .map((entry) => ({ ...entry, native_name: entry.native_name || entry.name || entry.code }))
}

// Last catalogue the API returned, or the bundled one when it has never answered.
export function getLanguages() {
  return readCatalogue()
}

export function saveLanguages(list) {
  const entries = normalizeCatalogue(list)
  if (!entries.length) return getLanguages()
  catalogueCache = entries
  // A full or blocked store must not cost the session the catalogue it just fetched.
  try {
    localStorage.setItem(LANGUAGE_CATALOGUE_KEY, JSON.stringify(entries))
  } catch {
    /* kept in memory for this session only */
  }
  return entries
}

// The is_default row, falling back to the first entry when the API marks none.
export function defaultLanguageCode(languages = getLanguages()) {
  return (languages.find((entry) => entry.is_default) ?? languages[0])?.code ?? "en"
}

export function isSupportedLanguage(code, languages = getLanguages()) {
  return languages.some((entry) => entry.code === code)
}

// Global, like AppManager's UserDefaults language. An explicit choice wins while it is
// still in the catalogue; otherwise the browser locale, otherwise the default language.
// Pass a catalogue to re-resolve against a freshly fetched one.
export function getLanguage(languages = getLanguages()) {
  const stored = localStorage.getItem(LANGUAGE_KEY)
  if (stored && isSupportedLanguage(stored, languages)) return stored
  return browserLanguage(languages) ?? defaultLanguageCode(languages)
}

// Prefix match, mirroring the backend's ja-JP to ja resolution.
function browserLanguage(languages) {
  const preferred = navigator.languages?.length ? navigator.languages : [navigator.language]
  for (const tag of preferred) {
    const base = String(tag ?? "").toLowerCase().split("-")[0]
    const match = languages.find((entry) => entry.code === base)
    if (match) return match.code
  }
  return null
}

export function saveLanguage(code) {
  localStorage.setItem(LANGUAGE_KEY, code)
}
